/* eslint-disable no-prototype-builtins */
import { withFormValidation } from '@updater-components/form-element';
import {
  withHTMLComponent,
  applyAsAttributes,
} from '@updater-legacy/components/custom-element';
import '@updater-components/fa-icon';

const html = `
  <div class="hit-area">
    <input />
    <button type="button">
      <upd-fa-icon name="calendar-alt"></upd-fa-icon>
    </button>
  </div>
  <upd-datepicker-popup></upd-datepicker-popup>
`;

const css = `
button,
input {
  box-sizing: border-box;
  font-size: 1rem;
  font-family: inherit;
  font-weight: var(--upd-datepicker-input-font-weight, inherit);
}

:host {
  display: inline-block;
  position: relative;
}

:host([disabled]) {
  opacity: 0.65;
  pointer-events: none;
  cursor: not-allowed;
  text-shadow: none;
  -webkit-user-select: none;
     -moz-user-select: none;
      -ms-user-select: none;
          user-select: none;
}

.hit-area {
  display: inline-flex;
  color: white;
  width: 100%;
}

button {
  outline: 0;
  font-weight: 200;
  text-align: center;
  border: 0.0625rem solid #bababb;
  color: #2b7499;
  padding: 0.75em 1.6em;
  display: inline-block;
  margin-left: -0.0625em;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
}

button:hover,
button:focus {
  border-color: #2b7499;
}

:host([disabled]) button:hover,
:host([disabled]) button:focus {
  border-color: #bababb;
}

:host(.upd-tile) button {
  background: transparent;
  border: 0;
  border-left: 0.0625rem solid #efefef;
  padding: 0.875em 1.6em;
}

upd-fa-icon {
  color: #2b7499;
  font-size: 0.875em;
}

input {
  cursor: default;
  background-color: white;
  border: 0.0625em solid #cccccc;
  border-radius: 0;
  width: 100%;
  padding: 0.375em 0.75em;
  display: block;
  transition: border-color ease-in-out 0.15s, box-shadow ease-in-out 0.15s;
  color: #404041;
}

:host(.upd-tile) input {
  background: transparent;
  border: 0;
  font-weight: 300;
}

:host(.upd-tile) input::-moz-placeholder {
  color: #bababb;
  font-weight: 300;
}

:host(.upd-tile) input:-ms-input-placeholder {
  color: #bababb;
  font-weight: 300;
}

:host(.upd-tile) input::-ms-input-placeholder {
  color: #bababb;
  font-weight: 300;
}

:host(.upd-tile) input::placeholder {
  color: #bababb;
  font-weight: 300;
}

:host([disabled]) input {
  background-color: #e4e4e5;
}
`;

function format(dateString) {
  if (!dateString || !dateString.length) {
    return '';
  }

  const regex = /(\d\d\d\d)-(\d\d)-(\d\d)/;
  const results = regex.exec(dateString);
  if (!results) {
    return '';
  }

  const [, year, month, date] = results;
  return `${month}/${date}/${year.substr(2, 2)}`;
}

const properties = ['value', 'min', 'max', 'name', 'required', 'placeholder'];
class DatepickerInput extends withFormValidation(
  withHTMLComponent(css, html)()
) {
  static observedAttributes = properties;

  static reflectedAttributes = properties;

  static attributeTypes = {
    required: 'boolean',
  };

  constructor() {
    super();

    this._input = this.shadowRoot.querySelector('input');
    this._hitarea = this.shadowRoot.querySelector('.hit-area');
    this._datepicker = this.shadowRoot.querySelector('upd-datepicker-popup');
  }

  connected() {
    // we have to wait for the child datepickerpopup to be constructed
    setTimeout(() => this._updateInput(), 0);

    this._addListeners();
  }

  disconnected() {
    this._removeListeners();
  }

  _addListeners() {
    // TODO: Replace the input with a long-date-input and actually use the value
    this._input.addEventListener('input', this._updateInput);
    this._datepicker.addEventListener('change', this._handleDateChange);
    this._hitarea.addEventListener('click', this._openDatepicker);
  }

  _removeListeners() {
    this._input.removeEventListener('input', this._updateInput);
    this._datepicker.removeEventListener('change', this._handleDateChange);
    this._hitarea.removeEventListener('click', this._openDatepicker);
  }

  _handleDateChange = () => {
    this.value = this._datepicker.value;
    this._updateInput();
    this.dispatchEvent(new Event('input'));
  };

  _updateInput = () => {
    this._input.value = format(this.value);
  };

  _openDatepicker = () => {
    if (this.hasAttribute('disabled')) {
      return;
    }

    this._datepicker.open();
  };

  set enabledDates(value) {
    this._datepicker.enabledDates = value;
  }

  get enabledDates() {
    return this._datepicker.enabledDates;
  }

  set disabledDates(value) {
    this._datepicker.disabledDates = value;
  }

  get disabledDates() {
    return this._datepicker.disabledDates;
  }

  updated({ value, min, max, name, required, placeholder }) {
    applyAsAttributes(this._datepicker, {
      value,
      min,
      max,
    });

    applyAsAttributes(this._input, {
      name,
      required,
      placeholder: placeholder || 'mm/dd/yy',
    });

    this._updateInput();
  }
}

customElements.define('upd-datepicker-input', DatepickerInput);
